import { computed, Injectable } from "@angular/core";
import { ArticleInfos } from "../../models/article.model";
import { EntityStoreService } from "../../../../shared/store/entity-store/entity-store.service";
import {
  EntityMetadata,
  EntityStoreState,
} from "../../../../shared/store/store-types.model";
import { ArticlesInfosFacadeService } from "./article-infos-facade.service";

@Injectable({ providedIn: "root" })
export class ArticlesInfosStoreService extends EntityStoreService<
  ArticleInfos,
  EntityMetadata,
  ArticleInfosState
> {
  constructor() {
    super({
      ...EntityStoreService.initialState,
    });
  }

  /**
   * Index par clé, reconstruits une seule fois par changement du store.
   *
   * POURQUOI. Ces trois accès étaient des `.find()` linéaires sur tout le
   * catalogue — plus de 650 fiches sur une base client. Ils sont appelés depuis
   * ~200 endroits, dont des liaisons de gabarit à l'intérieur de boucles : une
   * liste de 20 lignes qui résout l'unité de chaque article faisait
   * 20 × 9 × 650 comparaisons de chaînes À CHAQUE cycle de détection. Comme les
   * composants concernés sont en détection par défaut, ce cycle se rejoue au
   * moindre mouvement de souris.
   *
   * SÉMANTIQUE PRÉSERVÉE. `Array.find` rend la PREMIÈRE occurrence ; une `Map`
   * construite naïvement rendrait la DERNIÈRE. D'où le `has()` avant chaque
   * `set()` — sans lui, un catalogue portant deux fiches de même code changerait
   * de comportement en silence.
   */
  private readonly indexParArticleId = computed(() =>
    this.indexer((e) => e.articleId),
  );
  private readonly indexParCode = computed(() => this.indexer((e) => e.code));
  private readonly indexParEAN14 = computed(() => this.indexer((e) => e.ean14));

  private indexer(
    cle: (e: ArticleInfos) => string | null | undefined,
  ): Map<string, ArticleInfos> {
    const index = new Map<string, ArticleInfos>();
    for (const e of this.allEntities()) {
      const k = cle(e);
      if (k && !index.has(k)) index.set(k, e);
    }
    return index;
  }

  getByArticleId(articleId: string) {
    if (!articleId) {
      return computed(() => null);
    }
    return computed(() => this.indexParArticleId().get(articleId) ?? null);
  }

  getByCode(code: string) {
    return computed(() => this.indexParCode().get(code) ?? null);
  }

  getByEAN14(ean14: string) {
    if (!ean14) {
      throw new Error("ean14 null");
    }
    return computed(() => this.indexParEAN14().get(ean14) ?? null);
  }
}

export interface ArticleInfosState
  extends EntityStoreState<ArticlesInfosFacadeService> {}
